import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

export type Props = {
  /** The page's own name, after "Pollinator |" */
  title?: string;
  /** What a search result or a shared link says about the page */
  description?: string;
};

/**
 * The title, canonical address and sharing tags of a page.
 *
 * The canonical address is this page's path on the origin it is served from,
 * without its query or fragment, so a link shared with tracking parameters
 * or a section anchor still counts as the one page. The defaults for link
 * previews are in index.html, because most link previewers read only the
 * page as served and run none of this.
 */
/** The site's own description, as index.html gives it, for a page without one. */
export const SITE_DESCRIPTION = "Pollinator is a growing network of aligned peers.";

export function PageMeta({ title, description = SITE_DESCRIPTION }: Props) {
  const { pathname } = useLocation();
  const fullTitle = ["Pollinator", title].filter(Boolean).join(" | ");
  const url = `${window.location.origin}${pathname}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <link rel="canonical" href={url} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="description" content={description} />}
      {description && <meta property="og:description" content={description} />}
      {description && <meta name="twitter:description" content={description} />}
    </Helmet>
  );
}

export default PageMeta;
